/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect} from 'react'
import {useQuery} from 'react-query'
import moment from 'moment'
import {Graphic4Type} from '../core/_models'
import {getGraphic4} from '../core/_requests'
import {useAuth} from '../../../../app/modules/auth'
import {TOKENS} from '../../../components/design-system'

type Props = {
  className: string
  chartColor: string
  chartHeight: string
}

const Graphic4: React.FC<Props> = ({ className }) => {
  const {currentUser} = useAuth()

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    'total_boxes-project',
    async () => await getGraphic4(currentUser!.last_company_id, {take: 0, skip: 25}),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      // Polling at 60s as a safety net — the LogBox list also invalidates on
      // alarm_changed WS events via the logs-management QueryResponseProvider,
      // but this widget has its own query so we keep a slow refetch as backup.
      refetchInterval: 60_000,
    }
  )

  const logs = response as Graphic4Type[]

  useEffect(() => {
    refetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  const headerStyle: React.CSSProperties = {
    padding: '8px 6px',
    textAlign: 'left',
    borderBottom: `1px solid ${TOKENS.color.border}`,
    background: TOKENS.color.card,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }
  const cellStyle: React.CSSProperties = {
    padding: '6px',
    borderBottom: `1px solid ${TOKENS.color.divider}`,
  }

  return (
    <div
      className={className}
      style={{
        // Caps the widget height so the dashboard stays compact when the
        // table has many entries. Header stays sticky while the body scrolls.
        maxHeight: 320,
        overflow: 'auto',
      }}
    >
      {(logs && logs.length > 0) ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{
              fontSize: TOKENS.typography.label.size,
              fontWeight: TOKENS.typography.label.weight,
              textTransform: TOKENS.typography.label.transform,
              letterSpacing: TOKENS.typography.label.tracking,
              color: TOKENS.color.muted,
            }}>
              <th style={headerStyle}>Nº CMB</th>
              <th style={headerStyle}>Projeto</th>
              <th style={headerStyle}>Usuário</th>
              <th style={headerStyle}>Data/Hora</th>
              <th style={headerStyle}>Descrição</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => (
              <tr key={index} style={{ color: TOKENS.color.text }}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{log.cmb_number}</td>
                <td style={{ ...cellStyle, color: TOKENS.color.muted }}>{log.project_name}</td>
                <td style={{ ...cellStyle, color: TOKENS.color.muted }}>{log.user_name}</td>
                <td style={{ ...cellStyle, color: TOKENS.color.muted, whiteSpace: 'nowrap' }}>
                  {moment(log.created_at).format('DD/MM/YY HH:mm')}
                </td>
                <td style={cellStyle}>{log.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className='d-flex flex-1 justify-content-center align-items-center fs-7' style={{ color: '#9ca3af', minHeight: 180 }}>
          {isFetching ? (
            <div className="spinner-border text-secondary spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : 'Sem dados'}
        </div>
      )}
    </div>
  )
}

export { Graphic4 }
